import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibRadio } from '@chewy/kib-choices-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import { Box, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import { type Nil, Text, useFields, Utils } from '@pbt/pbt-ui-components'

import RequiredFieldsNotice from '~/components/common/inputs/RequiredFieldsNotice'
import TaskStateLabel from '~/components/common/TaskStateLabel'
import { TaskStatusSelectV2 } from '~/components/dashboard/tasks-dashboard/TaskStatusSelectV2'
import FeatureToggle from '~/constants/featureToggle'
import { useCreatableTaskTypes } from '~/store/hooks/tasks'
import { getCurrentBusinessWellnessPlansEnabled } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import type { ContactSlot, DataHandleWithUnsavedChanges, Task } from '~/types'
import { isFieldValuesChanged } from '~/utils'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import type { BaseTemplateInputHandle } from '../template-inputs/BaseTemplateInput/BaseTemplateInput'
import { getPatientHasMembership } from '../wellness-plans/wellnessPlanUtils'
import { TaskFormV1 } from './common/TaskFormV1'
import { TaskFormV2 } from './common/TaskFormV2'
import type { TaskRecurrenceSettingsHandle } from './TaskRecurrenceSettings'

import '@chewy/kib-fields-styles/dist/styles/kib-input-group.css'
import '@chewy/kib-choices-styles/dist/styles/kib-radio.css'
import styles from './Task.module.scss'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    detailsRow: {
      borderBottom: theme.constants.outlinedInputBorder,
    },
    listbox: {
      maxHeight: 190,
    },
  }),
  { name: 'TaskComponent' },
)

export interface TaskProps {
  appointmentId?: string
  className?: string
  clientId?: string | Nil
  dueDate?: string | Nil
  forClientActive?: boolean
  patientId?: string | Nil
  shortTaskFlow?: boolean
  shouldUseVCRBehavior?: boolean
  soapId?: string
  task?: Task
}

export interface TaskHandle extends DataHandleWithUnsavedChanges<Task> {}

const TaskComponent = forwardRef<TaskHandle, TaskProps>(function TaskComponent(
  {
    className,
    clientId,
    patientId,
    appointmentId,
    dueDate: dueDateProp,
    shouldUseVCRBehavior,
    soapId,
    task,
    forClientActive = false,
    shortTaskFlow = false,
  },
  ref,
) {
  const classes = useStyles()

  const wellnessPlansEnabled = useSelector(
    getCurrentBusinessWellnessPlansEnabled,
  )
  const { t } = useTranslation(['Common', 'Dialogs', 'Tasks', 'Tooltips'])

  const rootRef = useRef<HTMLDivElement>(null)
  const taskRecurrenceSettingsRef = useRef<TaskRecurrenceSettingsHandle>(null)
  const notesRef = useRef<BaseTemplateInputHandle>(null)
  const instructionsRef = useRef<BaseTemplateInputHandle>(null)

  const isTaskImprovementsPhase1Enabled = useSelector(
    getFeatureToggle(FeatureToggle.TASKS_IMPROVEMENTS_PHASE_1),
  )

  const isCreate = !task?.id
  const CreatableTaskTypes = useCreatableTaskTypes()
  const OtherType = Utils.findConstantIdByName('Other', CreatableTaskTypes)

  const isContextItem = useIsCurrentContextItem(task)

  const getClientId = () => clientId || task?.clientId || task?.client
  const getPatientId = () => patientId || task?.patientId || task?.patient
  const getAppointmentId = () =>
    appointmentId || task?.appointmentId || task?.appointment || null
  const getContactId = () => task?.contactId || task?.contact
  const getAssigned = () => task?.assignedId || task?.assigned
  const getSlotInfo = () => ({
    clientId: getClientId(),
    patientId: getPatientId(),
    contactId: getContactId(),
  })

  const [slotInfo, setSlotInfo] = useState<ContactSlot>(getSlotInfo())

  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    string | null
  >(getAppointmentId())

  const {
    clientId: selectedClientId,
    patientId: selectedPatientId,
    contactId: selectedContactId,
  } = slotInfo

  const { fields, validate, reset, touched } = useFields(
    [
      {
        name: 'forClient',
        label: t('Tasks:LABEL.CLIENT_TASK'),
        type: 'toggle',
        initialValue: isCreate ? forClientActive : task.forClient,
      },
      {
        name: 'name',
        label: t('Common:TASK_TITLE'),
        validators: ['required'],
        initialValue: task?.name || '',
      },
      {
        name: 'typeId',
        label: t('Common:TYPE_ONE'),
        initialValue: isCreate ? OtherType : task.typeId,
      },
      {
        name: 'assigneeId',
        label: isTaskImprovementsPhase1Enabled
          ? t('Common:ASSIGN_TO')
          : t('Common:ASSIGN_TEAM_MEMBER'),
        initialValue: getAssigned() || '',
      },
      ...(isTaskImprovementsPhase1Enabled
        ? [
            {
              name: 'copyToIds',
              label: t('Common:COPY_TO'),
              initialValue: task?.sharedToPersonIds || [],
            },
          ]
        : []),
      {
        name: 'notes',
        label: t('Common:NOTES'),
        initialValue: task?.notes || '',
      },
      {
        name: 'instructions',
        label: t('Common:INSTRUCTIONS_FOR_PET_PARENT'),
        initialValue: task?.instructions || '',
      },
      {
        name: 'clientNotes',
        label: t('Common:PET_PARENT_NOTES'),
        initialValue: task?.clientNotes || '',
      },
    ],
    false,
  )

  const {
    forClient,
    name,
    typeId,
    assigneeId,
    copyToIds,
    notes,
    instructions,
    clientNotes,
  } = fields

  useEffect(() => {
    setSlotInfo({ ...slotInfo, clientId })
  }, [clientId])

  useEffect(() => {
    setSlotInfo({ ...slotInfo, patientId })
  }, [patientId])

  const createTask = (): Task => ({
    id: task?.id as string,
    forClient: forClient.value,
    name: name.value,
    typeId: typeId.value,
    assigneeId: forClient.value ? selectedClientId : assigneeId.value,
    appointmentId: selectedAppointmentId,
    clientId: selectedClientId,
    ...(isTaskImprovementsPhase1Enabled
      ? { sharedToPersonIds: copyToIds.value }
      : {}),
    patientId: selectedPatientId,
    contactId: selectedContactId,
    notes: notes.value,
    instructions: instructions.value,
    soapId: selectedAppointmentId ? soapId || task?.soapId : null,

    ...(isCreate
      ? {}
      : {
          // for existing task if appointmentId was removed we need to explicitly
          // nullify order related fields
          orderId: selectedAppointmentId ? task?.orderId : null,

          orderType: selectedAppointmentId ? task?.orderType : null,

          // preserve existing state
          stateId: task.stateId,
        }),

    ...(taskRecurrenceSettingsRef?.current?.get() || {}),
  })

  useEffect(() => {
    reset()
    setSelectedAppointmentId(getAppointmentId())
    setSlotInfo(getSlotInfo())
    notesRef.current?.setInitialValue(task?.notes)
    instructionsRef.current?.setInitialValue(task?.instructions)
  }, [task])

  useEffect(() => {
    if (!selectedClientId && !selectedPatientId && !shortTaskFlow) {
      setSelectedAppointmentId(null)
    }
  }, [selectedClientId, selectedPatientId, shortTaskFlow])

  const validateTaskRecurrenceSettings = () =>
    taskRecurrenceSettingsRef.current
      ? taskRecurrenceSettingsRef.current.validate()
      : true

  const selectedPatient = useSelector(getPatient(selectedPatientId))
  const patientHasMembership = getPatientHasMembership(selectedPatient)

  const assigneeClientAndPatientIsValid =
    !forClient.value ||
    Boolean(selectedClientId && selectedPatientId) ||
    patientHasMembership

  useImperativeHandle(ref, () => ({
    validate: () =>
      validate() &&
      validateTaskRecurrenceSettings() &&
      assigneeClientAndPatientIsValid,
    get: createTask,
    hasUnsavedChanges: () => {
      const fieldsChanged = isFieldValuesChanged(fields)
      const recurrenceSettingsChanged =
        taskRecurrenceSettingsRef.current?.hasUnsavedChanges() ?? false
      const slotInfoChanged = !R.whereEq(slotInfo, getSlotInfo())
      const appointmentChanged = selectedAppointmentId !== getAppointmentId()

      return (
        fieldsChanged ||
        recurrenceSettingsChanged ||
        slotInfoChanged ||
        appointmentChanged
      )
    },
  }))

  const paddingLeftCreation = isCreate ? 3 : 2
  const paddingRightCreation = isCreate ? 3 : 9

  return (
    <Grid
      container
      item
      alignItems="flex-end"
      className={classNames(className, classes.root)}
      pl={isTaskImprovementsPhase1Enabled ? paddingLeftCreation : 3}
      pr={isTaskImprovementsPhase1Enabled ? paddingRightCreation : 3}
      ref={rootRef}
    >
      {!isCreate && (
        <Grid
          container
          item
          alignItems="center"
          pb={isTaskImprovementsPhase1Enabled ? 2 : undefined}
        >
          {task?.forClient && (
            <>
              {isTaskImprovementsPhase1Enabled ? (
                <span className={styles.forClient}>{forClient.label}</span>
              ) : (
                <Text strong ml={0} mr={1} variant="body2">
                  {forClient.label}
                </Text>
              )}
            </>
          )}
          {isTaskImprovementsPhase1Enabled ? (
            <TaskStatusSelectV2
              task={task}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <TaskStateLabel stateId={task.stateId} />
          )}
        </Grid>
      )}
      {isCreate && wellnessPlansEnabled && (
        <Grid container item direction="column">
          {isTaskImprovementsPhase1Enabled ? (
            <KibInputGroup
              aria-label="workflow-radio-group"
              className={styles.title}
              label={t('Common:WORKFLOW_ONE')}
            >
              <Box display="flex" gap={2}>
                <KibRadio
                  disabled={!isContextItem}
                  label={t('Common:INTERNAL')}
                  name="workflow-radio-group"
                  selectedValue={Number(forClient.value)}
                  value={0}
                  onChange={(newValue) => forClient.setValue(Boolean(newValue))}
                />
                <KibRadio
                  disabled={!isContextItem}
                  label={t('Common:CLIENT')}
                  name="workflow-radio-group"
                  selectedValue={Number(forClient.value)}
                  value={1}
                  onChange={(newValue) => forClient.setValue(Boolean(newValue))}
                />
              </Box>
            </KibInputGroup>
          ) : (
            <>
              <Text strong mt={1} variant="body2">
                {t('Common:WORKFLOW_ONE')}
              </Text>
              <RadioGroup
                row
                aria-label="workflow-radio-group"
                name="workflow-radio-group"
                value={Boolean(forClient.value)}
                onChange={() => forClient.setValue(!forClient.value)}
              >
                <FormControlLabel
                  disableTypography
                  control={<Radio />}
                  disabled={!isContextItem}
                  label={t('Common:INTERNAL')}
                  value={false}
                />
                <FormControlLabel
                  disableTypography
                  value
                  control={<Radio />}
                  disabled={!isContextItem}
                  label={t('Common:CLIENT')}
                />
              </RadioGroup>
            </>
          )}
        </Grid>
      )}

      {isTaskImprovementsPhase1Enabled ? (
        <TaskFormV2
          assigneeClientAndPatientIsValid={assigneeClientAndPatientIsValid}
          dueDate={dueDateProp}
          fields={{
            name,
            notes,
            instructions,
            copyToIds,
            assigneeId,
            typeId,
            touched,
            clientNotes,
            forClient,
          }}
          instructionsRef={instructionsRef}
          notesRef={notesRef}
          rootRef={rootRef}
          selectedAppointmentId={selectedAppointmentId}
          selectedClientId={selectedClientId}
          selectedPatientId={selectedPatientId}
          setSelectedAppointmentId={setSelectedAppointmentId}
          setSlotInfo={setSlotInfo}
          shouldUseVCRBehavior={shouldUseVCRBehavior}
          slotInfo={slotInfo}
          soapId={soapId}
          task={task}
          taskRecurrenceSettingsRef={taskRecurrenceSettingsRef}
        />
      ) : (
        <TaskFormV1
          assigneeClientAndPatientIsValid={assigneeClientAndPatientIsValid}
          dueDate={dueDateProp}
          fields={{
            assigneeId,
            forClient,
            instructions,
            clientNotes,
            name,
            notes,
            typeId,
          }}
          instructionsRef={instructionsRef}
          notesRef={notesRef}
          rootRef={rootRef}
          selectedAppointmentId={selectedAppointmentId}
          selectedClientId={selectedClientId}
          selectedPatientId={selectedPatientId}
          setSelectedAppointmentId={setSelectedAppointmentId}
          setSlotInfo={setSlotInfo}
          shouldUseVCRBehavior={shouldUseVCRBehavior}
          slotInfo={slotInfo}
          soapId={soapId}
          task={task}
          taskRecurrenceSettingsRef={taskRecurrenceSettingsRef}
          touched={touched}
        />
      )}

      {isCreate && (
        <Grid item mt={1}>
          <RequiredFieldsNotice
            variant={
              isTaskImprovementsPhase1Enabled ? 'lowAccent3' : 'lowAccent4'
            }
          />
        </Grid>
      )}
    </Grid>
  )
})

export default TaskComponent
